'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  Compass,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Trophy,
  RefreshCw,
  Lightbulb,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const NAV_ITEMS = [
  {
    key: 'nav-discovery',
    href: '/segment-discovery',
    icon: Compass,
    label: 'Discover Segments',
    badge: null,
  },
  {
    key: 'nav-targets',
    href: '/target-segments',
    icon: Bookmark,
    label: 'My Targets',
    badge: 7,
  },
  {
    key: 'nav-kom-suggestions',
    href: '/kom-suggestions',
    icon: Lightbulb,
    label: 'KOM Suggestions',
    badge: 6,
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`relative flex flex-col bg-white border-r border-[hsl(var(--border))] transition-all duration-300 ease-in-out shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-[hsl(var(--border))] px-3 ${collapsed ? 'justify-center' : 'gap-2'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-[15px] text-[hsl(var(--foreground))] tracking-tight whitespace-nowrap">
            KOM Gatherers
          </span>
        )}
      </div>
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-[72px] z-10 w-6 h-6 rounded-full bg-white border border-[hsl(var(--border))] flex items-center justify-center shadow-sm hover:bg-[hsl(var(--muted))] transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight size={12} className="text-[hsl(var(--muted-foreground))]" />
        ) : (
          <ChevronLeft size={12} className="text-[hsl(var(--muted-foreground))]" />
        )}
      </button>
      {/* Nav section */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {!collapsed && (
          <p className="px-2 mb-2 text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
            Segments
          </p>
        )}
        {NAV_ITEMS?.map((item) => {
          const Icon = item?.icon;
          const isActive = pathname === item?.href;
          return (
            <Link
              key={item?.key}
              href={item?.href}
              className={`group relative flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))]'
                  : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
              }`}
            >
              <Icon
                size={18}
                className={`shrink-0 ${isActive ? 'text-[hsl(var(--primary))]' : ''}`}
              />
              {!collapsed && (
                <span className="flex-1 whitespace-nowrap">{item?.label}</span>
              )}
              {!collapsed && item?.badge !== null && (
                <span className="ml-auto text-[10px] font-semibold bg-[hsl(var(--primary))] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                  {item?.badge}
                </span>
              )}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 text-xs font-medium bg-[hsl(var(--foreground))] text-white rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                  {item?.label}
                </span>
              )}
            </Link>
          );
        })}

        {!collapsed && (
          <p className="px-2 mt-4 mb-2 text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
            Account
          </p>
        )}
        <button
          className="group relative w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] transition-all duration-150"
        >
          <RefreshCw size={18} className="shrink-0" />
          {!collapsed && <span className="whitespace-nowrap">Sync Strava</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 text-xs font-medium bg-[hsl(var(--foreground))] text-white rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
              Sync Strava
            </span>
          )}
        </button>
        <Link
          href="/profile"
          className={`group relative flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
            pathname === '/profile' ?'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))]' :'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
          }`}
        >
          <User size={18} className={`shrink-0 ${pathname === '/profile' ? 'text-[hsl(var(--primary))]' : ''}`} />
          {!collapsed && <span className="whitespace-nowrap">Profile</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 text-xs font-medium bg-[hsl(var(--foreground))] text-white rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
              Profile
            </span>
          )}
        </Link>
      </nav>
      {/* User footer */}
      <div className={`border-t border-[hsl(var(--border))] p-3 ${collapsed ? 'flex justify-center' : ''}`}>
        {collapsed ? (
          <div className="group relative">
            <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary)/0.1)] flex items-center justify-center cursor-pointer">
              <User size={14} className="text-[hsl(var(--primary))]" />
            </div>
            <span className="absolute left-full ml-2 px-2 py-1 text-xs font-medium bg-[hsl(var(--foreground))] text-white rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
              Marcus Tanner
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary)/0.1)] flex items-center justify-center shrink-0">
              <User size={14} className="text-[hsl(var(--primary))]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[hsl(var(--foreground))] truncate">Marcus Tanner</p>
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">Connected via Strava</p>
            </div>
            <button className="p-1 rounded hover:bg-[hsl(var(--muted))] transition-colors" title="Sign out">
              <LogOut size={13} className="text-[hsl(var(--muted-foreground))]" />
            </button>
          </div>
        )}
      </div>
      {/* KOM trophy accent */}
      {!collapsed && (
        <div className="mx-3 mb-3 p-3 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100">
          <div className="flex items-center gap-2">
            <Trophy size={14} className="text-amber-500 shrink-0" />
            <div>
              <p className="text-[10px] font-semibold text-amber-700">KOMs Held</p>
              <p className="text-sm font-bold text-amber-600 font-mono tabular-nums">3 segments</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}